"use client";

import { JSX, useId, useMemo, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { CascadeIn } from "@/components/motion/cascade-in";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import type { ModuleFaqData } from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import { MAX_ITEMS_PER_CATEGORY } from "@/config/limits";

function comparePositions(a: string, b: string): number {
  const as = a.split(".").map(Number);
  const bs = b.split(".").map(Number);
  for (let i = 0; i < Math.max(as.length, bs.length); i++) {
    const av = as[i];
    const bv = bs[i];
    if (av === undefined) return -1;
    if (bv === undefined) return 1;
    if (Number.isNaN(av) || Number.isNaN(bv)) return a.localeCompare(b);
    if (av !== bv) return av - bv;
  }
  return 0;
}

function PlusMinus({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      className="shrink-0"
      aria-hidden
    >
      <path
        d="M3 8h10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M8 3v10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        className="origin-center transition-transform duration-200 [transform-box:fill-box] motion-reduce:transition-none"
        style={{ transform: open ? "scaleY(0)" : "scaleY(1)" }}
      />
    </svg>
  );
}

export default function ModuleFaq({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleFaqData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element | null {
  const baseId = useId();
  const reduceMotion = useReducedMotion() ?? false;

  const entries = useMemo(() => {
    const sorted = Object.entries(data ?? {})
      .filter(([, item]) => item != null)
      .sort(([a], [b]) => comparePositions(a, b));
    const takenPerCategory = new Map<string, number>();
    return sorted.filter(([, item]) => {
      const category = item.category ?? "";
      const taken = takenPerCategory.get(category) ?? 0;
      if (taken >= MAX_ITEMS_PER_CATEGORY) return false;
      takenPerCategory.set(category, taken + 1);
      return true;
    });
  }, [data]);

  const [openId, setOpenId] = useState<string | null>(null);

  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          entries
            .map(([, item]) => item.category)
            .filter((category): category is string => Boolean(category)),
        ),
      ),
    [entries],
  );

  const [selectedCategory, setSelectedCategory] = useState<string>(
    () => categories[0] ?? "",
  );

  // The initialiser runs once, so a later data change that drops the selected
  // category would leave a rendered tab bar above an empty list.
  const activeCategory = categories.includes(selectedCategory)
    ? selectedCategory
    : categories[0] ?? "";

  const tabOptions = useMemo(
    () =>
      categories.map((category) => ({
        value: category,
        label: stripInlineMarkdown(category),
        count: entries.filter(([, item]) => item.category === category).length,
      })),
    [categories, entries],
  );

  const visible = useMemo(() => {
    const all = entries.map(([key, item], index) => ({ key, item, index }));
    // No category on any item means there is nothing to filter by, so filtering
    // here would leave the reader with an empty list instead of the full one.
    if (categories.length === 0) return all;
    return all.filter(({ item }) => item.category === activeCategory);
  }, [categories, entries, activeCategory]);

  if (entries.length === 0) return null;

  return (
    <div
      className={cn("flex min-h-0 w-full min-w-0 flex-1 flex-col", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      {categories.length > 1 && (
        <div className="mb-2 w-full shrink-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <SegmentTabs
            value={activeCategory}
            onValueChange={(next) => {
              if (!next) return;
              setOpenId(null);
              setSelectedCategory(next);
            }}
            options={tabOptions}
          />
        </div>
      )}

      <CascadeIn
        keySignature={`faq-${activeCategory}`}
        className="w-full min-w-0 min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
        itemClassName="w-full min-w-0"
      >
        {visible.map(({ key, item, index }, i) => {
          const open = openId === key;
          const contentId = `${baseId}-${key}-answer`;
          const triggerId = `${baseId}-${key}-question`;
          return (
            <div
              key={key}
              className={cn(
                "w-full min-w-0",
                i > 0 && "border-t border-dashed border-[var(--border-dashed)]",
              )}
            >
              <button
                type="button"
                id={triggerId}
                aria-expanded={open}
                aria-controls={contentId}
                onClick={(e) => {
                  setOpenId(open ? null : key);
                  emitModuleInteraction(
                    e.currentTarget,
                    open ? "deselect" : "select",
                    { kind: "row", id: key, index },
                  );
                }}
                className="group flex w-full items-center justify-between gap-4 py-4 text-left"
              >
                <span
                  className={cn(
                    "text-[15px] leading-snug transition-colors",
                    MODULE_LABEL_WRAP.heading,
                    open
                      ? "font-medium text-[var(--fg)]"
                      : "text-[var(--muted)] group-hover:text-[var(--fg)]",
                  )}
                >
                  {stripInlineMarkdown(item.question)}
                </span>
                <span
                  className={cn(
                    "transition-colors",
                    open ? "text-[var(--fg)]" : "text-[var(--subtle)]",
                  )}
                >
                  <PlusMinus open={open} />
                </span>
              </button>
              <motion.div
                id={contentId}
                role="region"
                aria-labelledby={triggerId}
                aria-hidden={!open}
                inert={!open}
                initial={false}
                animate={{ height: open ? "auto" : 0 }}
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
                }
                // Clipping has to stay on for the whole animation, otherwise the
                // answer spills out of the shrinking box while it collapses.
                className="overflow-hidden"
              >
                <p
                  className={cn(
                    "pb-4 pr-8 text-sm leading-relaxed text-[var(--muted)]",
                    MODULE_LABEL_WRAP.body,
                  )}
                >
                  {stripInlineMarkdown(item.answer)}
                </p>
              </motion.div>
            </div>
          );
        })}
      </CascadeIn>
    </div>
  );
}
